import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["searchInput", "sortSelect", "tagsContainer", "expandToggle", "clearSearch"]

    // Private field for search debounce
    #searchTimer = null

    connect() {
        this.toggleSearchClear()
    }

    disconnect() {
        window.clearTimeout(this.#searchTimer)
    }

    handleSortChange(event) {
        const sortValue = event.target.value
        const currentUrl = new URL(window.location.href)

        if (sortValue && sortValue !== 'newest') {
            currentUrl.searchParams.set('sort', sortValue)
        } else {
            currentUrl.searchParams.delete('sort')
        }
        currentUrl.searchParams.delete("page")
        this.navigate(currentUrl.toString())
    }

    handleRegionChange(event) {
        const region = event.currentTarget.dataset.region
        const currentUrl = new URL(window.location.href)

        if (region) {
            currentUrl.searchParams.set('region', region)
        } else {
            currentUrl.searchParams.delete('region')
        }
        // Always reset to page 1 when changing region
        currentUrl.searchParams.delete('page')
        this.navigate(currentUrl.toString())
    }

    handleTagChange(event) {
        const targetPath = event.currentTarget.dataset.url
        const currentUrl = new URL(window.location.href)
        const targetUrl = new URL(targetPath, window.location.origin)

        // Preserve region if present
        const region = currentUrl.searchParams.get('region')
        if (region) targetUrl.searchParams.set('region', region)

        // Preserve salary and other active filters if present
        ['salary', 'benefits', 'estimated', 'sort', 'search'].forEach(param => {
            const value = currentUrl.searchParams.get(param)
            if (value) targetUrl.searchParams.set(param, value)
        })

        targetUrl.searchParams.delete("page")
        this.navigate(targetUrl.toString())
    }

    toggleSearchClear() {
        if (!this.hasSearchInputTarget || !this.hasClearSearchTarget) return

        if (this.searchInputTarget.value.trim()) {
            this.clearSearchTarget.classList.remove("hidden")
        } else {
            this.clearSearchTarget.classList.add("hidden")
        }
    }

    submitSearchOnEnter(event) {
        if (event.key !== "Enter") return
        event.preventDefault()
        this.applySearch()
    }

    applySearch(event) {
        if (event) event.preventDefault()
        if (!this.hasSearchInputTarget) return
        window.clearTimeout(this.#searchTimer)
        this.toggleSearchClear()
        this.navigate(this.searchUrl().toString())
    }

    clearSearch(event) {
        if (event) event.preventDefault()
        if (!this.hasSearchInputTarget) return

        this.searchInputTarget.value = ""
        this.toggleSearchClear()
        this.navigate(this.searchUrl().toString())
        this.searchInputTarget.focus()
    }

    // Backward-compatible for existing markup that still binds input->job-filters#search.
    search(event) {
        this.toggleSearchClear()
        if (!event) return

        window.clearTimeout(this.#searchTimer)
        this.#searchTimer = window.setTimeout(() => {
            this.navigate(this.searchUrl(event.target).toString())
        }, 500)
    }

    searchUrl(input = null) {
        const currentUrl = new URL(window.location.href)
        const searchValue = (input || (this.hasSearchInputTarget ? this.searchInputTarget : null))?.value?.trim() || ""

        if (searchValue) {
            currentUrl.searchParams.set("search", searchValue)
        } else {
            currentUrl.searchParams.delete("search")
        }

        currentUrl.searchParams.delete("page")
        return currentUrl
    }

    navigate(url) {
        const frame = document.querySelector("turbo-frame#job_results")
        if (frame && window.Turbo && typeof window.Turbo.visit === "function") {
            frame.dispatchEvent(new CustomEvent("devfound:filter-loading-start", { bubbles: true }))
            window.Turbo.visit(url, { frame: frame.id, action: "advance" })
            return
        }

        window.location.href = url
    }

    updateExpandToggle(toggleButton, extraCount, isExpanded) {
        const visibleText = isExpanded ? "Less" : `+${extraCount}`
        const accessibleName = isExpanded ? "Less filters" : `+${extraCount} more filters`
        const arrow = isExpanded ? "▲" : "▼"

        toggleButton.innerHTML = `<span class="expand-text">${visibleText}</span> <span class="expand-arrow">${arrow}</span>`
        toggleButton.setAttribute("aria-label", accessibleName)
        toggleButton.setAttribute("aria-expanded", String(isExpanded))
    }

    toggleTagsExpansion() {
        if (!this.hasTagsContainerTarget || !this.hasExpandToggleTarget) return

        const container = this.tagsContainerTarget
        const toggleButton = this.expandToggleTarget
        const isExpanded = toggleButton.classList.toggle('expanded')
        const extras = Array.from(container.querySelectorAll('.extra-filter-tag'))

        this.updateExpandToggle(toggleButton, extras.length, isExpanded)

        if (isExpanded) {
            extras.forEach((el) => {
                el.classList.remove('hidden')
                el.style.display = 'inline-flex'
            })
        } else {
            extras.forEach((el) => {
                el.classList.add('hidden')
                el.style.display = 'none'
            })
        }
    }
}
